import { normalizeGoogleAdsCustomerId } from '@/utils/googleAdsUtils'
import { CheckCircle, Edit, ExternalLink, Plus } from 'lucide-react'
import { SiGoogleads } from 'react-icons/si'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsAdGroupManagement,
  type GoogleAdsAdGroupManagementResult,
} from 'sprites-types/workflow/tools/googleAdsAdGroupManagement'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleAdsAdGroupManagementDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsAdGroupManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsAdGroupManagementResult
  const isCreate = 'adGroupId' in result && result.adGroupId !== null
  const adGroupId = 'adGroupId' in result ? result.adGroupId : undefined
  const resourceName = 'resourceName' in result ? result.resourceName : undefined
  const message = 'message' in result ? result.message : undefined

  // Extract customerId and campaignId from inputs for Google Ads link
  const inputs = nodeExecution.inputs.functionArgs as any
  const params = inputs?.action?.params || {}
  const customerId = params.customerId
  const campaignId = params.campaignId
  const adGroupName = params.name

  // Extract adGroupId from resourceName if not directly available
  const finalAdGroupId = adGroupId || (resourceName ? resourceName.split('/').pop() : undefined)

  // Normalize customer ID for Google Ads URL (removes dashes and "customers/" prefix)
  let normalizedCustomerId: string | undefined
  if (customerId) {
    try {
      normalizedCustomerId = normalizeGoogleAdsCustomerId(customerId)
    } catch {
      // If normalization fails, normalizedCustomerId remains undefined and link won't render
    }
  }

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-start gap-4 px-4 py-3">
        <div className="mt-1 flex-shrink-0">
          <div className="rounded-full bg-green-100 p-2">
            {isCreate ? (
              <Plus className="h-5 w-5 text-green-600" />
            ) : (
              <Edit className="h-5 w-5 text-green-600" />
            )}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex items-center gap-2">
            <SiGoogleads className="h-4 w-4 text-[#4285f4]" />
            <div className="text-sm font-semibold text-gray-900">
              {isCreate ? 'Ad Group Created' : 'Ad Group Updated'}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
            {adGroupName && (
              <div className="mb-1 text-sm font-medium text-gray-900">{adGroupName}</div>
            )}
            <div className="space-y-1 text-xs">
              {finalAdGroupId && <div className="text-gray-500">Ad Group ID: {finalAdGroupId}</div>}
              {campaignId && <div className="text-gray-500">Campaign ID: {campaignId}</div>}
              {normalizedCustomerId && finalAdGroupId && (
                <div className="text-gray-500">
                  <a
                    href={`https://ads.google.com/aw/adgroups?adGroupId=${finalAdGroupId}&__c=${normalizedCustomerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    View in Google Ads
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              {message && <div className="text-gray-700">{message}</div>}
            </div>
          </div>

          {isCreate && (
            <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <CheckCircle className="h-3 w-3 text-green-600" />
              <span>Ad group is ready to use</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function downloadGoogleAdsAdGroupManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsAdGroupManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsAdGroupManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as GoogleAdsAdGroupManagementResult
  const isCreate = 'adGroupId' in result && result.adGroupId !== null
  const resourceName = 'resourceName' in result ? result.resourceName : undefined
  const message = 'message' in result ? result.message : undefined

  return `## Google Ads Ad Group ${isCreate ? 'Created' : 'Updated'}

${resourceName ? `**Resource:** ${resourceName}` : ''}
${message ? `**Message:** ${message}` : ''}

---
`
}

export const GoogleAdsAdGroupManagementDisplay = createWorkflowToolComponent(
  GoogleAdsAdGroupManagementDisplayComponent,
  downloadGoogleAdsAdGroupManagement,
  googleAdsAdGroupManagementToMarkdown
)
